// src/contexts/AuthContext.jsx - FIXED VERSION with proper function scoping
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN_START':
            return { ...state, loading: true, error: null };

        case 'LOGIN_SUCCESS':
            const user = action.payload.user;
            // Ensure entitlement is set
            if (!user.entitlement && user.plan) {
                user.entitlement = user.plan;
            }

            return {
                ...state,
                loading: false,
                isAuthenticated: true,
                user: user,
                token: action.payload.token,
                rememberMe: action.payload.rememberMe
            };

        case 'LOGIN_ERROR':
            return { ...state, loading: false, error: action.payload };

        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                token: null,
                rememberMe: false,
                loading: false,
                error: null
            };

        case 'UPDATE_USER':
            const currentUser = state.user;
            const updates = action.payload;

            // Proper entitlement handling during updates
            let newEntitlement = updates.entitlement;
            if (!newEntitlement && updates.plan) {
                newEntitlement = updates.plan;
            } else if (!newEntitlement) {
                newEntitlement = currentUser?.entitlement || currentUser?.plan;
            }

            const updatedUser = {
                ...currentUser,
                ...updates,
                entitlement: action.payload.entitlement || action.payload.plan || currentUser?.entitlement || currentUser?.plan || 'free'
            };

            // console.log('🔄 AuthReducer UPDATE_USER:', {
            //     current: {
            //         plan: currentUser?.plan,
            //         entitlement: currentUser?.entitlement
            //     },
            //     updates: {
            //         plan: updates.plan,
            //         entitlement: updates.entitlement
            //     },
            //     final: {
            //         plan: updatedUser.plan,
            //         entitlement: updatedUser.entitlement
            //     }
            // });

            return {
                ...state,
                user: updatedUser
            };

        case 'SET_REMEMBER_ME':
            return { ...state, rememberMe: action.payload };

        case 'SET_LOADING':
            return { ...state, loading: action.payload };

        default:
            return state;
    }
};

export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, {
        isAuthenticated: false,
        user: null,
        token: null,
        rememberMe: false,
        loading: true, // Start with loading true
        error: null,
    });


    // Add this to your AuthContext.jsx
    const verify2FA = async (email, code, rememberMe) => {
        try {
            const response = await fetch('/api/auth/verify-2fa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code }),
            });

            const data = await response.json();

            if (data.success) {
                const { token, user } = data;

                // Use existing token storage logic
                storeToken(token, rememberMe, user);

                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        user,
                        token,
                        rememberMe
                    }
                });

                return { success: true };
            } else {
                return { success: false, error: data.message };
            }
        } catch (error) {
            console.error('2FA verification error:', error);
            return { success: false, error: 'Verification failed' };
        }
    };

    // FIXED: Move refreshUser function inside component to access state and dispatch
    const refreshUser = async () => {
        // console.log('🔄 Refreshing user data...');

        if (!state.token) {
            console.warn('⚠️ Cannot refresh user - no token available');
            return;
        }

        try {
            const response = await authAPI.validateToken();

            if (response.data?.user) {
                const userData = response.data.user;

                // Ensure entitlement is set properly
                if (!userData.entitlement && userData.plan) {
                    userData.entitlement = userData.plan;
                }

                dispatch({
                    type: 'UPDATE_USER',
                    payload: userData
                });

                // Update stored user info if remember me is enabled
                if (state.rememberMe && localStorage.getItem('userInfo')) {
                    localStorage.setItem('userInfo', JSON.stringify({
                        id: userData.id,
                        email: userData.email,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        plan: userData.plan,
                        entitlement: userData.entitlement,
                        role: userData.role,
                        isFlagged: userData.isFlagged,
                        isBlocked: userData.isBlocked,
                        status: userData.status
                    }));
                }

                // console.log('✅ User data refreshed successfully:', {
                //     plan: userData.plan,
                //     entitlement: userData.entitlement,
                //     email: userData.email
                // });

                return userData;
            } else {
                console.warn('⚠️ No user data in refresh response');
            }
        } catch (error) {
            console.error('❌ Failed to refresh user data:', error);

            // If token is invalid, logout
            if (error.response?.status === 401) {
                // console.log('🔐 Token invalid during refresh, logging out');
                logout();
            }

            throw error;
        }
    };

    // ENHANCED: Check for existing token on app start
    useEffect(() => {
        let isMounted = true; // Prevent state updates if component unmounted

        const validateExistingToken = async () => {
            const { token, rememberMe, source } = getStoredToken();

            if (!token) {
                // console.log('🔍 No existing token found');
                if (isMounted) {
                    dispatch({ type: 'SET_LOADING', payload: false });
                }
                return;
            }

            try {
                // ENHANCED: Add timeout to prevent hanging
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

                const response = await authAPI.validateToken();
                clearTimeout(timeoutId);

                if (!isMounted) return; // Component unmounted

                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        user: response.data.user,
                        token,
                        rememberMe
                    }
                });

            } catch (error) {
                if (!isMounted) return; // Component unmounted

                console.warn('❌ Token validation failed:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data
                });

                // Clear invalid token
                clearStoredToken();

                dispatch({
                    type: 'LOGOUT'
                });
            } finally {
                if (isMounted) {
                    dispatch({ type: 'SET_LOADING', payload: false });
                }
            }
        };

        validateExistingToken();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, []); // Only run once on mount

    useEffect(() => {
        // Check if we just returned from a payment flow
        const urlParams = new URLSearchParams(window.location.search);
        const hasPaymentParams = urlParams.get('success') || urlParams.get('canceled') || urlParams.get('session_id');

        if (hasPaymentParams) {
            // console.log('🔄 Returned from payment flow, validating authentication...');

            // Force token validation after payment redirect
            const validateAfterPayment = async () => {
                const { token } = getStoredToken();

                if (!token) {
                    console.error('❌ No token found after payment redirect');

                    // Show a message and redirect to login
                    if (window.confirm('Your session expired during payment. Please log in again to view your updated subscription.')) {
                        window.location.href = '/login?returnUrl=' + encodeURIComponent(window.location.pathname + window.location.search);
                    }
                    return;
                }

                try {
                    // console.log('🔄 Validating token after payment...');
                    const response = await authAPI.validateToken();

                    if (response.data?.user) {
                        // console.log('✅ Token valid after payment, updating user data');

                        dispatch({
                            type: 'LOGIN_SUCCESS',
                            payload: {
                                user: response.data.user,
                                token,
                                rememberMe: localStorage.getItem('rememberMe') === 'true'
                            }
                        });
                    } else {
                        console.error('❌ Invalid token response after payment');
                        clearStoredToken();
                        window.location.href = '/login';
                    }

                } catch (error) {
                    console.error('❌ Token validation failed after payment:', error);
                    clearStoredToken();

                    if (window.confirm('Authentication failed after payment. Please log in again to view your subscription status.')) {
                        window.location.href = '/login?returnUrl=' + encodeURIComponent(window.location.pathname + window.location.search);
                    }
                }
            };

            // Validate immediately if we have payment params
            validateAfterPayment();
        }
    }, [window.location.search]);

    // Enhanced token storage with payment flow awareness
    const storeToken = (token, rememberMe = false, user = null) => {
        // console.log(`🔐 Storing token with rememberMe: ${rememberMe}`);

        if (rememberMe) {
            // Store in localStorage for persistent login
            localStorage.setItem('token', token);
            localStorage.setItem('rememberMe', 'true');
            localStorage.setItem('loginTime', new Date().toISOString());

            if (user) {
                localStorage.setItem('userInfo', JSON.stringify({
                    id: user.id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    plan: user.plan,
                    entitlement: user.entitlement,
                    role: user.role
                }));
            }

            // Clear any session storage
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('loginTime');

            // console.log('✅ Token stored in localStorage (persistent)');
        } else {
            // Store in sessionStorage for session-only login
            sessionStorage.setItem('token', token);
            sessionStorage.setItem('loginTime', new Date().toISOString());

            // Also store in localStorage as backup for payment redirects
            localStorage.setItem('token', token);
            localStorage.setItem('tempToken', 'true'); // Mark as temporary

            // Clear localStorage remember me data but keep token temporarily
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('userInfo');

            // console.log('✅ Token stored in both sessionStorage and localStorage (temporary backup)');
        }
    };

    // Enhanced token cleanup
    const clearStoredToken = () => {
        // console.log('🔐 Clearing all stored tokens');

        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('loginTime');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('tempToken');

        // Clear sessionStorage
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('loginTime');

        // console.log('✅ All tokens cleared');
    };

    // Enhanced token retrieval that handles payment redirects
    const getStoredToken = () => {
        // Check localStorage first (remember me = true)
        const localToken = localStorage.getItem('token');
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const tempToken = localStorage.getItem('tempToken') === 'true';

        if (localToken && (rememberMe || tempToken)) {
            return {
                token: localToken,
                rememberMe: rememberMe && !tempToken,
                source: tempToken ? 'localStorage_temp' : 'localStorage'
            };
        }

        // Check sessionStorage
        const sessionToken = sessionStorage.getItem('token');
        if (sessionToken) {
            return { token: sessionToken, rememberMe: false, source: 'sessionStorage' };
        }

        // Fallback to localStorage without remember me check (for payment redirects)
        if (localToken) {
            // console.log('🔐 Using localStorage token as fallback (payment redirect scenario)');
            return { token: localToken, rememberMe: false, source: 'localStorage_fallback' };
        }

        // console.log('🔐 No stored token found');
        return { token: null, rememberMe: false, source: null };
    };

    const login = async (credentials) => {
        // console.log(`🔐 Login attempt for ${credentials.email}, rememberMe: ${credentials.rememberMe}`);

        dispatch({ type: 'LOGIN_START' });

        try {
            const response = await authAPI.login({
                email: credentials.email,
                password: credentials.password
            });

            // console.log('📡 Login API response:', {
            //     success: response.data.success,
            //     hasToken: !!response.data.token,
            //     hasUser: !!response.data.user
            // });

            // Handle 2FA requirement - THIS IS THE KEY FIX
            if (response.data.requires2FA) {
                // Don't dispatch any auth state changes, just return the 2FA flag
                return {
                    success: true,
                    requires2FA: true,
                    message: response.data.message
                };
            }

            // Standard login flow (no 2FA) - token should be present
            if (!response.data.token) {
                throw new Error('Login failed - no token received');
            }

            // Handle 2FA requirement
            if (response.data.requires2FA) {
                return { success: true, requires2FA: true };
            }

            const { token, user } = response.data;

            // Store token based on remember me preference
            storeToken(token, credentials.rememberMe, user);

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token,
                    rememberMe: credentials.rememberMe
                }
            });

            // console.log(`✅ Login successful, rememberMe: ${credentials.rememberMe}`);
            return { success: true };

        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Login failed';
            console.error('❌ Login failed:', {
                message,
                status: error.response?.status,
                data: error.response?.data
            });

            dispatch({ type: 'LOGIN_ERROR', payload: message });
            return { success: false, error: message };
        }
    };

    const register = async (userData) => {
        dispatch({ type: 'LOGIN_START' });

        try {
            const response = await authAPI.register(userData);

            if (!response.data.success || !response.data.token) {
                throw new Error(response.data.message || 'Registration failed - no token received');
            }

            const { token, user } = response.data;

            // Default to session storage for new registrations
            storeToken(token, false, user);

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token,
                    rememberMe: false
                }
            });

            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Registration failed';
            dispatch({ type: 'LOGIN_ERROR', payload: message });
            return { success: false, error: message };
        }
    };

    const logout = () => {
        // console.log('🔐 Logging out user');
        clearStoredToken();
        dispatch({ type: 'LOGOUT' });
    };

    const updateUser = (userData) => {
        // console.log('🔄 Updating user data:', userData);

        // Ensure entitlement is properly handled
        const updatedUserData = {
            ...userData,
            // If plan is updated but entitlement isn't specified, set entitlement to plan
            entitlement: userData.entitlement || (userData.plan ? userData.plan : state.user?.entitlement)
        };

        dispatch({ type: 'UPDATE_USER', payload: updatedUserData });

        // Update stored user info if remember me is enabled
        if (state.rememberMe && localStorage.getItem('userInfo')) {
            const currentUserInfo = JSON.parse(localStorage.getItem('userInfo'));
            const newUserInfo = {
                ...currentUserInfo,
                ...updatedUserData
            };

            console.log('💾 Updating stored user info:', newUserInfo);
            localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
        }

        console.log('✅ User data updated successfully');
    };

    // Toggle remember me for current session
    const toggleRememberMe = (rememberMe) => {
        if (state.token) {
            console.log(`🔐 Toggling rememberMe to: ${rememberMe}`);
            storeToken(state.token, rememberMe, state.user);
            dispatch({ type: 'SET_REMEMBER_ME', payload: rememberMe });
        }
    };

    // Debug function to get current storage state
    const getStorageDebugInfo = () => {
        return {
            currentState: {
                isAuthenticated: state.isAuthenticated,
                hasUser: !!state.user,
                hasToken: !!state.token,
                rememberMe: state.rememberMe,
                loading: state.loading,
                userEmail: state.user?.email
            },
            localStorage: {
                token: localStorage.getItem('token') ? 'Present' : 'None',
                rememberMe: localStorage.getItem('rememberMe') || 'None',
                loginTime: localStorage.getItem('loginTime') || 'None',
                userInfo: localStorage.getItem('userInfo') ? 'Present' : 'None'
            },
            sessionStorage: {
                token: sessionStorage.getItem('token') ? 'Present' : 'None',
                loginTime: sessionStorage.getItem('loginTime') || 'None'
            }
        };
    };

    // Check if token is about to expire (for remember me tokens)
    const checkTokenExpiry = () => {
        const loginTime = localStorage.getItem('loginTime') || sessionStorage.getItem('loginTime');
        if (!loginTime) return { expired: false, daysLeft: null };

        const loginDate = new Date(loginTime);
        const now = new Date();
        const daysDiff = Math.floor((now - loginDate) / (1000 * 60 * 60 * 24));
        const maxDays = state.rememberMe ? 30 : 1; // 30 days for remember me, 1 day for session

        return {
            expired: daysDiff >= maxDays,
            daysLeft: Math.max(0, maxDays - daysDiff),
            loginDate: loginDate.toISOString()
        };
    };

    return (
        <AuthContext.Provider value={{
            ...state,
            login,
            register,
            logout,
            updateUser,
            refreshUser, // Now properly scoped within component
            toggleRememberMe,
            getStorageDebugInfo,
            checkTokenExpiry,
            // Utility functions for debugging
            clearAllStorage: clearStoredToken,
            getStoredToken,
            verify2FA
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Export additional utility functions for debugging
export const debugStorage = () => {
    console.group('🔍 Storage Debug Info');

    console.log('localStorage:', {
        token: localStorage.getItem('token') ? 'Present' : 'None',
        rememberMe: localStorage.getItem('rememberMe'),
        loginTime: localStorage.getItem('loginTime'),
        userInfo: localStorage.getItem('userInfo') ? 'Present' : 'None',
        allKeys: Object.keys(localStorage)
    });

    console.log('sessionStorage:', {
        token: sessionStorage.getItem('token') ? 'Present' : 'None',
        loginTime: sessionStorage.getItem('loginTime'),
        allKeys: Object.keys(sessionStorage)
    });

    console.groupEnd();
};

// Clear all storage (useful for debugging)
export const clearAllStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    console.log('✅ All browser storage cleared');
};